import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  CONTACT_ROLES,
  EMPTY_CONTACT,
  addContact,
  contactProblems,
  listContacts,
  updateContact,
  type Contact,
  type ContactInput,
} from "../../api/ankhCustomers";

/**
 * Named contacts (owner 2026-11-04): "quest have two email address and two
 * contact. One is owner and other is accountant ... and it may be more".
 */

const roleLabel = (code: string) => CONTACT_ROLES.find((r) => r.code === code)?.label ?? code;

/** One contact's fields. Address is optional and folded under "Address". */
export function ContactFields({ value, onChange }: { value: ContactInput; onChange: (c: ContactInput) => void }) {
  const [showAddress, setShowAddress] = useState(Boolean(value.address || value.city || value.state || value.pin));
  const set = (k: keyof ContactInput) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value });
  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField select size="small" label="Role" value={value.role} onChange={set("role")} sx={{ width: { sm: 140 } }}>
          {CONTACT_ROLES.map((r) => (
            <MenuItem key={r.code} value={r.code}>
              {r.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField required size="small" label="Name" value={value.name} onChange={set("name")} sx={{ flex: 1 }} />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField size="small" label="Email" value={value.email} onChange={set("email")} sx={{ flex: 1 }} />
        <TextField size="small" label="Mobile" value={value.mobile} onChange={set("mobile")} sx={{ width: { sm: 160 } }} />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControlLabel
          control={<Checkbox size="small" checked={value.receivesinvoices} onChange={(e) => onChange({ ...value, receivesinvoices: e.target.checked })} />}
          label="Receives invoices"
        />
        {!showAddress && (
          <Button size="small" onClick={() => setShowAddress(true)}>
            + Address
          </Button>
        )}
      </Stack>
      {showAddress && (
        <Stack spacing={1}>
          <TextField size="small" label="Address" value={value.address} onChange={set("address")} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField size="small" label="City" value={value.city} onChange={set("city")} sx={{ flex: 1 }} />
            <TextField size="small" label="State" value={value.state} onChange={set("state")} sx={{ flex: 1 }} />
            <TextField size="small" label="PIN" value={value.pin} onChange={set("pin")} sx={{ width: { sm: 110 } }} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

/** A list of new contacts, as offline onboarding collects them. */
export function ContactListEditor({ value, onChange }: { value: ContactInput[]; onChange: (cs: ContactInput[]) => void }) {
  return (
    <Stack spacing={2}>
      {value.map((c, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <ContactFields value={c} onChange={(n) => onChange(value.map((x, j) => (j === i ? n : x)))} />
            </Box>
            <IconButton aria-label="Remove contact" disabled={value.length === 1} onClick={() => onChange(value.filter((_, j) => j !== i))}>
              ×
            </IconButton>
          </Stack>
        </Paper>
      ))}
      <Box>
        <Button size="small" onClick={() => onChange([...value, { ...EMPTY_CONTACT, role: value.length === 0 ? "owner" : "accountant" }])}>
          + Add contact
        </Button>
      </Box>
    </Stack>
  );
}

const asInput = (c: Contact): ContactInput => ({
  role: c.role, name: c.name, email: c.email, mobile: c.mobile, address: c.address, city: c.city, state: c.state, pin: c.pin,
  receivesinvoices: c.receivesinvoices,
});

/** Contacts of an open billing account: list, add, edit, remove (never deleted). */
export function ContactsDialog({ accountId, name, onClose }: { accountId: string; name: string; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const key = ["ankh-contacts", accountId];
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => listContacts(identity, accountId, signal),
    enabled: Boolean(identity.token),
    retry: false,
  });
  const [editing, setEditing] = useState<{ id: string | null; value: ContactInput } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  async function run(f: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await f();
      await qc.invalidateQueries({ queryKey: key });
      setEditing(null);
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(false);
    }
  }

  const problems = editing ? contactProblems(editing.value) : [];
  const rows = (list.data ?? []).filter((c) => showRemoved || c.active);
  const removed = (list.data ?? []).filter((c) => !c.active).length;

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contacts — {name}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
        {list.isError && <Alert severity="error" sx={{ mb: 2 }}>Could not read the contacts: {(list.error as Error).message}</Alert>}
        {list.isLoading && <Typography variant="body2">Loading…</Typography>}
        {list.data && list.data.length === 0 && !editing && <Alert severity="info">No contacts yet.</Alert>}
        {rows.length > 0 && (
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small" aria-label="Contacts">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Invoices</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} sx={c.active ? undefined : { opacity: 0.5 }}>
                    <TableCell>{roleLabel(c.role)}</TableCell>
                    <TableCell>
                      {c.name}
                      {c.city && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {[c.address, c.city, c.state, c.pin].filter(Boolean).join(", ")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>{c.mobile}</TableCell>
                    <TableCell>{c.receivesinvoices ? <Chip size="small" label="Yes" /> : "—"}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {c.active ? (
                        <>
                          <Button size="small" disabled={busy} onClick={() => setEditing({ id: c.id, value: asInput(c) })}>
                            Edit
                          </Button>
                          <Button size="small" color="inherit" disabled={busy}
                            onClick={() => void run(() => updateContact(identity, accountId, c.id, { ...asInput(c), active: false }))}>
                            Remove
                          </Button>
                        </>
                      ) : (
                        <Button size="small" disabled={busy}
                          onClick={() => void run(() => updateContact(identity, accountId, c.id, { ...asInput(c), active: true }))}>
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {removed > 0 && (
          <FormControlLabel
            control={<Checkbox size="small" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />}
            label={`Show removed (${removed})`}
          />
        )}
        {editing ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editing.id ? "Edit contact" : "New contact"}
            </Typography>
            <ContactFields value={editing.value} onChange={(value) => setEditing({ ...editing, value })} />
            {problems.length > 0 && editing.value.name.trim() !== "" && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                {problems.join(" · ")}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button disabled={busy} onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button variant="contained" disabled={busy || problems.length > 0}
                onClick={() =>
                  void run(() =>
                    editing.id
                      ? updateContact(identity, accountId, editing.id, { ...editing.value, active: true })
                      : addContact(identity, accountId, editing.value),
                  )
                }>
                Save
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Button size="small" disabled={busy || !list.data} onClick={() => setEditing({ id: null, value: { ...EMPTY_CONTACT } })}>
            + Add contact
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
